import { useEffect, useState, type ReactNode } from "react";
import { Link } from "wouter";
import { Bell, BellOff, Check, CheckCircle2, Loader2 } from "lucide-react";
import { trpc, queryClient } from "../lib/trpc";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { fmtRelative } from "../lib/format";
import { cn } from "../lib/utils";
import { usePushSubscription, type PushSubscriptionState } from "../lib/usePush";
import { isDeliveryFailure } from "../../../shared/notification-feed";

export default function Notifications() {
  const { t } = useLanguage();
  const listQ = trpc.notification.myList.useQuery();
  const markRead = trpc.notification.markRead.useMutation({
    onSuccess: () => listQ.refetch(),
  });
  const push = usePushSubscription();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">
            {t({ "zh-TW": "通知中心", "en-US": "Notifications", "zh-CN": "通知中心" })}
          </h1>
          <p className="text-[13px] text-muted-foreground">
            {t({ "zh-TW": "Web Push 即時推播 + Email 備援。", "en-US": "Web Push real-time + email fallback.", "zh-CN": "Web Push 即时推播 + Email 备援。" })}
          </p>
        </div>
        <PushSubscribeControl {...push} />
      </div>

      <div className="space-y-2">
        {(listQ.data ?? []).length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-10 text-center">
            <p className="font-display text-base text-muted-foreground">
              {t({ "zh-TW": "目前沒有通知", "en-US": "No notifications", "zh-CN": "目前没有通知" })}
            </p>
          </div>
        ) : (
          (listQ.data ?? []).map((n) => {
            const url = (n.payload as { url?: string } | null)?.url;
            const isUnread = !n.viewedAt && n.status !== "viewed";
            const inner = (
              <Card className={isUnread ? "border-primary/40 bg-primary-soft/30" : ""}>
                <CardContent className="flex items-start gap-3 p-4">
                  <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-background">
                    <Bell className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <p className="font-medium">{n.title}</p>
                      {/* 未啟用推播造成的失敗不顯示 —— 那不是系統故障，站內通知照常運作 */}
                      {isDeliveryFailure(n) && (
                        <Badge variant="destructive" size="sm">
                          {t({ "zh-TW": "發送失敗", "en-US": "Failed", "zh-CN": "发送失败" })}
                        </Badge>
                      )}
                    </div>
                    <p className="text-[13px] text-foreground/80">{n.body}</p>
                    <p className="font-mono text-[12px] text-muted-foreground">{fmtRelative(n.createdAt)}</p>
                  </div>
                  {isUnread && (
                    <Button
                      variant="ghost"
                      size="iconSm"
                      onClick={(e) => {
                        e.preventDefault();
                        markRead.mutate({ id: n.id });
                      }}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
            return url ? (
              <Link key={n.id} href={url.replace(/^https?:\/\/[^/]+/, "")}>
                <a onClick={() => isUnread && markRead.mutate({ id: n.id })}>{inner}</a>
              </Link>
            ) : (
              <div key={n.id}>{inner}</div>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * 瀏覽器推播開關（2026-08-29 重寫）
 * 舊版只有「啟用／停用」兩顆按鈕：按下去到成功之間沒有任何變化，權限被拒或
 * 伺服器沒設金鑰時更是完全靜默。現在每種狀態都有文字＋顏色，並顯示處理中與錯誤。
 */
function PushSubscribeControl({ status, error, justChanged, subscribe, unsubscribe }: PushSubscriptionState) {
  const { t } = useLanguage();

  const StatusPill = ({
    tone,
    icon,
    children,
  }: {
    tone: "on" | "off" | "warn" | "muted";
    icon: ReactNode;
    children: ReactNode;
  }) => (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-medium",
        tone === "on" &&
          "border-[color-mix(in_oklab,var(--color-success)_35%,transparent)] bg-[color-mix(in_oklab,var(--color-success)_15%,transparent)] text-[var(--color-success)]",
        tone === "warn" &&
          "border-[color-mix(in_oklab,var(--color-warning)_40%,transparent)] bg-[color-mix(in_oklab,var(--color-warning)_18%,transparent)] text-[var(--color-warning)]",
        tone === "off" && "border-border bg-muted text-muted-foreground",
        tone === "muted" && "border-border bg-background text-muted-foreground",
      )}
    >
      {icon}
      {children}
    </span>
  );

  return (
    <div className="flex flex-col items-start gap-1.5 sm:items-end">
      <div aria-live="polite" className="flex flex-wrap items-center gap-2">
        {status === "subscribed" && (
          <>
            <StatusPill tone="on" icon={<CheckCircle2 className="h-3.5 w-3.5" />}>
              {t({ "zh-TW": "推播已啟用", "en-US": "Push enabled", "zh-CN": "推播已启用" })}
            </StatusPill>
            <Button variant="outline" size="sm" onClick={unsubscribe}>
              <BellOff className="h-3.5 w-3.5" />
              {t({ "zh-TW": "停用", "en-US": "Disable", "zh-CN": "停用" })}
            </Button>
          </>
        )}

        {status === "idle" && (
          <>
            <StatusPill tone="off" icon={<BellOff className="h-3.5 w-3.5" />}>
              {t({ "zh-TW": "推播未啟用", "en-US": "Push off", "zh-CN": "推播未启用" })}
            </StatusPill>
            <Button size="sm" onClick={subscribe}>
              <Bell className="h-3.5 w-3.5" />
              {t({ "zh-TW": "啟用瀏覽器推播", "en-US": "Enable push", "zh-CN": "启用浏览器推播" })}
            </Button>
          </>
        )}

        {status === "working" && (
          <Button size="sm" disabled>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t({ "zh-TW": "處理中…", "en-US": "Working…", "zh-CN": "处理中…" })}
          </Button>
        )}

        {status === "denied" && (
          <StatusPill tone="warn" icon={<BellOff className="h-3.5 w-3.5" />}>
            {t({ "zh-TW": "瀏覽器已封鎖通知", "en-US": "Blocked by browser", "zh-CN": "浏览器已封锁通知" })}
          </StatusPill>
        )}

        {status === "unconfigured" && (
          <StatusPill tone="warn" icon={<BellOff className="h-3.5 w-3.5" />}>
            {t({ "zh-TW": "推播尚未開通", "en-US": "Push not configured", "zh-CN": "推播尚未开通" })}
          </StatusPill>
        )}

        {status === "unsupported" && (
          <StatusPill tone="muted" icon={<BellOff className="h-3.5 w-3.5" />}>
            {t({ "zh-TW": "此瀏覽器不支援", "en-US": "Not supported", "zh-CN": "此浏览器不支援" })}
          </StatusPill>
        )}
      </div>

      {/* 說明文字：只在需要使用者採取行動或有結果時出現 */}
      <p className="max-w-xs text-[12px] leading-relaxed text-muted-foreground sm:text-right">
        {justChanged === "enabled" &&
          t({
            "zh-TW": "已啟用，之後訂單有變動會直接跳出系統通知。",
            "en-US": "Enabled — you'll get system notifications when orders change.",
            "zh-CN": "已启用，之后订单有变动会直接跳出系统通知。",
          })}
        {justChanged === "disabled" &&
          t({ "zh-TW": "已停用瀏覽器推播。", "en-US": "Browser push disabled.", "zh-CN": "已停用浏览器推播。" })}
        {!justChanged && status === "denied" &&
          t({
            "zh-TW": "你先前拒絕了通知權限，瀏覽器不會再詢問。請點網址列左側的鎖頭圖示，把「通知」改成允許後重新整理。",
            "en-US": "You blocked notifications earlier and the browser won't ask again. Click the padlock in the address bar, allow Notifications, then reload.",
            "zh-CN": "你先前拒绝了通知权限，浏览器不会再询问。请点网址列左侧的锁头图示，把「通知」改成允许后重新整理。",
          })}
        {!justChanged && status === "unconfigured" &&
          t({
            "zh-TW": "系統尚未開通推播金鑰，請聯絡平台管理員。站內提醒與 Email 不受影響。",
            "en-US": "Push keys aren't configured yet — contact the platform admin. In-app alerts still work.",
            "zh-CN": "系统尚未开通推播金钥，请联络平台管理员。站内提醒与 Email 不受影响。",
          })}
        {!justChanged && status === "unsupported" &&
          t({
            "zh-TW": "此瀏覽器不支援 Web Push；站內提醒仍會正常顯示。",
            "en-US": "This browser doesn't support Web Push; in-app alerts still work.",
            "zh-CN": "此浏览器不支援 Web Push；站内提醒仍会正常显示。",
          })}
      </p>

      {error && (
        <p className="max-w-xs text-[12px] leading-relaxed text-destructive sm:text-right">
          {t({ "zh-TW": "啟用失敗：", "en-US": "Failed: ", "zh-CN": "启用失败：" })}
          {error}
        </p>
      )}
    </div>
  );
}

